"use client";

import { useMemo } from "react";
import { usePieceContext } from "./PieceContext";

type PieceKind = "ring" | "necklace" | "bracelet" | "earring" | "other";

type Props = {
  description?: string;
  pieceKind: PieceKind;
  inPossession: boolean;
  itemSize?: string;
};

const RING_SIZES = ((): string[] => {
  const sizes: string[] = [];
  for (let s = 3; s <= 13.5 + 1e-9; s += 0.5) {
    sizes.push(s % 1 === 0 ? `${s}` : s.toFixed(1));
  }
  return sizes;
})();

const NECKLACE_LENGTHS = ["16\"", "18\"", "20\"", "22\"", "24\""];
const BRACELET_SIZES = ["6\"", "6.5\"", "7\"", "7.5\"", "8\"", "8.5\""];

const PIECE_NOUN: Record<PieceKind, string> = {
  ring: "ring",
  necklace: "necklace",
  bracelet: "bracelet",
  earring: "pair",
  other: "piece"
};

export function PieceDetailsCard({ description, pieceKind, inPossession, itemSize }: Props) {
  const { requestedSize, setRequestedSize } = usePieceContext();

  const sizeConfig = useMemo(() => {
    if (pieceKind === "ring") {
      return { label: "Ring size", options: RING_SIZES, placeholder: "Keep as is" };
    }
    if (pieceKind === "necklace") {
      return { label: "Length", options: NECKLACE_LENGTHS, placeholder: "Keep as is" };
    }
    if (pieceKind === "bracelet") {
      return { label: "Bracelet length", options: BRACELET_SIZES, placeholder: "Keep as is" };
    }
    return null;
  }, [pieceKind]);

  const sizeNoun = pieceKind === "necklace" || pieceKind === "bracelet" ? "length" : "size";
  const pieceNoun = PIECE_NOUN[pieceKind];
  const trimmedSize = itemSize?.trim() ?? "";
  const hasItemSize = trimmedSize.length > 0;

  const hasDescription = Boolean(description && description.trim());
  const showCard = hasDescription || sizeConfig || !inPossession || hasItemSize;

  if (!showCard) return null;

  return (
    <div className="space-y-5 border border-slate-200 bg-white/90 p-6">
      {!inPossession && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This piece is not yet in our possession. Shipping times may be longer than expected.
        </div>
      )}

      {hasDescription && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Description</p>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{description}</p>
        </div>
      )}

      {hasItemSize && (
        <p className="text-sm leading-relaxed text-slate-700">
          This {pieceNoun} comes in {sizeNoun} <span className="font-semibold">{trimmedSize}</span>.
          {sizeConfig
            ? " If you would like to request an adjustment, please select a different option from the dropdown below — your request will be sent with your inquiry."
            : ` For a different ${sizeNoun}, please get in touch.`}
        </p>
      )}

      {sizeConfig && (
        <div className="space-y-2">
          <label
            htmlFor="piece-size-select"
            className="text-xs uppercase tracking-[0.3em] text-slate-500"
          >
            Requested {sizeConfig.label.toLowerCase()}
          </label>
          <select
            id="piece-size-select"
            value={requestedSize}
            onChange={(event) => setRequestedSize(event.target.value)}
            className="w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          >
            <option value="">{sizeConfig.placeholder}</option>
            {sizeConfig.options
              .filter((option) => option !== trimmedSize)
              .map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
          </select>
          {requestedSize ? (
            <p className="text-xs text-slate-500">
              We&rsquo;ll note this {sizeNoun} request when we follow up about your inquiry.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
